import { useState, useMemo } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { PlatformLayout } from "@/pages/platform/platform-layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  Wrench,
  FileSearch,
  Download,
  CheckCircle2,
  Archive,
  AlertTriangle,
  Gauge,
  MousePointerClick,
  Sparkles,
  Loader2,
  Image as ImageIcon,
  FileCode2,
  ChevronLeft,
  ClipboardCopy,
  Check,
} from "lucide-react";

// ── Types (mirror server payloads) ───────────────────────────────────────────
type ReportListItem = {
  id: string;
  agentId: string;
  runId: string | null;
  status: string;
  periodStart: string;
  periodEnd: string;
  model: string | null;
  costUsd: string | null;
  inputTokens: number | null;
  outputTokens: number | null;
  reviewedAt: string | null;
  createdAt: string;
};

type RollupError = {
  signature: string;
  title: string;
  occurrences: number;
  severity: string;
  sources: Record<string, number>;
  sampleMessage: string;
  sampleRoute: string | null;
  statusCodes: number[];
  browsers: string[];
  breadcrumbTrail: any[] | null;
  screenshotObjectPath: string | null;
  domSnapshotObjectPath: string | null;
  lastSeen: string;
  affectedWorkspaces: number;
};

type FullReport = ReportListItem & {
  brief: string | null;
  error: string | null;
  categories: Record<string, any[]> | null;
  rollup: {
    totals?: Record<string, number>;
    errors?: RollupError[];
    performance?: Array<{
      route: string | null;
      metric: string;
      samples: number;
      p50Ms: number;
      p95Ms: number;
      p99Ms: number;
      maxMs: number;
    }>;
    uxFriction?: {
      topActions?: Array<{ category: string; actionType: string; count: number }>;
      busiestRoutes?: Array<{ route: string | null; actions: number }>;
    };
    optimization?: {
      highTrafficRoutes?: Array<{ route: string | null; actions: number }>;
      errorDenseRoutes?: Array<{ route: string | null; errorOccurrences: number }>;
    };
  } | null;
};

const STATUS_STYLES: Record<string, string> = {
  ready: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  reviewed: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  generating: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  failed: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  archived: "bg-muted text-muted-foreground",
};

const SEV_STYLES: Record<string, string> = {
  critical: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  high: "bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300",
  medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  low: "bg-muted text-muted-foreground",
  error: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  warning: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
};

function fmtDate(s: string | null): string {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return s;
  }
}

function trailString(trail: any[] | null): string {
  if (!Array.isArray(trail) || trail.length === 0) return "—";
  return trail
    .slice(-12)
    .map((c) => `${c.category || "?"}:${c.actionType || c.action_type || "?"}`)
    .join(" → ");
}

export default function PlatformEngineerReports() {
  const { toast } = useToast();
  // agentId filter comes from the query string (?agentId=…) set by the agent card.
  const agentId = useMemo(() => {
    try {
      return new URLSearchParams(window.location.search).get("agentId");
    } catch {
      return null;
    }
  }, []);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listKey = agentId
    ? ["/api/platform/engineer-reports", { agentId }]
    : ["/api/platform/engineer-reports"];

  const { data: reports = [], isLoading } = useQuery<ReportListItem[]>({
    queryKey: listKey,
    queryFn: async () => {
      const url = agentId
        ? `/api/platform/engineer-reports?agentId=${encodeURIComponent(agentId)}`
        : "/api/platform/engineer-reports";
      const res = await apiRequest("GET", url);
      return res.json();
    },
  });

  const { data: report, isLoading: reportLoading } = useQuery<FullReport>({
    queryKey: ["/api/platform/engineer-reports", selectedId],
    enabled: !!selectedId,
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/platform/engineer-reports/${selectedId}`);
      return res.json();
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) =>
      apiRequest("PATCH", `/api/platform/engineer-reports/${id}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/engineer-reports"] });
      toast({ title: "Report updated" });
    },
    onError: () => toast({ title: "Could not update report", variant: "destructive" }),
  });

  const runDiagnosticsMutation = useMutation({
    mutationFn: async () =>
      apiRequest("POST", "/api/platform/engineer-reports/run", { windowDays: 7 }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/engineer-reports"] });
      toast({
        title: "Diagnostics started",
        description: "Pooling telemetry and drafting the engineering brief — the report appears here when ready.",
      });
    },
    onError: () => toast({ title: "Could not start diagnostics", variant: "destructive" }),
  });

  // ── Detail view ────────────────────────────────────────────────────────────
  if (selectedId) {
    return (
      <PlatformLayout>
        <div className="max-w-6xl mx-auto p-6 space-y-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSelectedId(null)}
            data-testid="button-back-to-reports"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to reports
          </Button>

          {reportLoading || !report ? (
            <div className="h-64 rounded-lg border bg-muted/30 animate-pulse" />
          ) : (
            <ReportDetail
              report={report}
              onReview={() => statusMutation.mutate({ id: report.id, status: "reviewed" })}
              onArchive={() => statusMutation.mutate({ id: report.id, status: "archived" })}
              reviewing={statusMutation.isPending}
            />
          )}
        </div>
      </PlatformLayout>
    );
  }

  // ── List view ──────────────────────────────────────────────────────────────
  return (
    <PlatformLayout>
      <div className="max-w-6xl mx-auto p-6 space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-primary/10 p-2.5">
              <Wrench className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold tracking-tight" data-testid="text-page-title">
                Engineering Log
              </h1>
              <p className="text-sm text-muted-foreground max-w-2xl">
                Weekly self-diagnostics brief pooled from cross-workspace telemetry.
                Human-reviewed; nothing is auto-applied.
              </p>
            </div>
          </div>
          <Button
            onClick={() => runDiagnosticsMutation.mutate()}
            disabled={runDiagnosticsMutation.isPending}
            data-testid="button-run-diagnostics"
          >
            {runDiagnosticsMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Wrench className="h-4 w-4 mr-2" />
            )}
            Run diagnostics now
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-20 rounded-lg border bg-muted/30 animate-pulse" />
            ))}
          </div>
        ) : reports.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="flex flex-col items-center justify-center py-16 text-center">
              <div className="rounded-full bg-muted p-4 mb-4">
                <FileSearch className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium">No reports yet</h3>
              <p className="text-sm text-muted-foreground max-w-sm mt-1">
                Click "Run diagnostics now" above to generate your first brief,
                or wait for the Monday-morning scheduled run.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {reports.map((r) => (
              <Card
                key={r.id}
                className="hover:border-primary/40 transition-colors cursor-pointer"
                onClick={() => r.status !== "generating" && setSelectedId(r.id)}
                data-testid={`card-report-${r.id}`}
              >
                <CardContent className="flex items-center justify-between gap-4 py-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="rounded-full bg-primary/10 p-2 shrink-0">
                      <Sparkles className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate" data-testid={`text-report-period-${r.id}`}>
                        {fmtDate(r.periodStart)} → {fmtDate(r.periodEnd)}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {r.model || "—"}
                        {r.costUsd != null && ` · $${Number(r.costUsd).toFixed(4)}`}
                        {r.inputTokens != null &&
                          ` · ${r.inputTokens + (r.outputTokens ?? 0)} tokens`}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.status === "generating" && (
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    )}
                    <Badge
                      variant="secondary"
                      className={STATUS_STYLES[r.status] ?? ""}
                      data-testid={`status-report-${r.id}`}
                    >
                      {r.status}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}

// ── Report detail with category tabs + error log ─────────────────────────────
function ReportDetail({
  report,
  onReview,
  onArchive,
  reviewing,
}: {
  report: FullReport;
  onReview: () => void;
  onArchive: () => void;
  reviewing: boolean;
}) {
  const rollup = report.rollup ?? {};
  const totals = rollup.totals ?? {};
  const errors = rollup.errors ?? [];
  const categories = report.categories ?? {};

  if (report.status === "failed") {
    return (
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" /> Diagnostics run failed
          </CardTitle>
        </CardHeader>
        <CardContent>
          <pre className="text-xs bg-muted p-3 rounded overflow-auto whitespace-pre-wrap">
            {report.error || "Unknown error"}
          </pre>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header + actions */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-semibold">
            {fmtDate(report.periodStart)} → {fmtDate(report.periodEnd)}
          </h2>
          <div className="text-sm text-muted-foreground mt-1">
            {report.model || "—"}
            {report.costUsd != null && ` · $${Number(report.costUsd).toFixed(4)}`}
            {report.inputTokens != null &&
              ` · ${report.inputTokens} in / ${report.outputTokens ?? 0} out tokens`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className={STATUS_STYLES[report.status] ?? ""}>
            {report.status}
          </Badge>
          <CopyForAgentButton reportId={report.id} />
          {report.status !== "reviewed" && (
            <Button
              variant="outline"
              size="sm"
              onClick={onReview}
              disabled={reviewing}
              data-testid="button-mark-reviewed"
            >
              <CheckCircle2 className="h-4 w-4 mr-2" /> Mark reviewed
            </Button>
          )}
          {report.status !== "archived" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onArchive}
              disabled={reviewing}
              data-testid="button-archive-report"
            >
              <Archive className="h-4 w-4 mr-2" /> Archive
            </Button>
          )}
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label="Error occurrences" value={totals.errorOccurrences ?? 0} />
        <Stat label="Distinct signatures" value={totals.distinctErrorSignatures ?? 0} />
        <Stat label="Perf samples" value={totals.perfSamples ?? 0} />
        <Stat label="Breadcrumbs" value={totals.breadcrumbs ?? 0} />
        <Stat label="Workspaces hit" value={totals.affectedWorkspaces ?? 0} />
      </div>

      <Tabs defaultValue="errorlog">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="errorlog" data-testid="tab-errorlog">
            <FileSearch className="h-4 w-4 mr-1.5" /> Error Log ({errors.length})
          </TabsTrigger>
          <TabsTrigger value="brief" data-testid="tab-brief">
            <Sparkles className="h-4 w-4 mr-1.5" /> Brief
          </TabsTrigger>
          <TabsTrigger value="errors" data-testid="tab-cat-errors">
            <AlertTriangle className="h-4 w-4 mr-1.5" /> Errors
          </TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-cat-performance">
            <Gauge className="h-4 w-4 mr-1.5" /> Performance
          </TabsTrigger>
          <TabsTrigger value="ux" data-testid="tab-cat-ux">
            <MousePointerClick className="h-4 w-4 mr-1.5" /> UX Friction
          </TabsTrigger>
          <TabsTrigger value="optimization" data-testid="tab-cat-optimization">
            <Sparkles className="h-4 w-4 mr-1.5" /> Optimization
          </TabsTrigger>
        </TabsList>

        {/* Error Log — the headline deliverable */}
        <TabsContent value="errorlog" className="space-y-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <p className="text-sm text-muted-foreground">
              Grouped by signature, with the repro breadcrumb trail and best-effort
              screenshot / masked DOM snapshot for each.
            </p>
            <div className="flex items-center gap-2">
              <a
                href={`/api/platform/engineer-reports/${report.id}/error-log/export?format=csv`}
                data-testid="link-export-csv"
              >
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" /> CSV
                </Button>
              </a>
              <a
                href={`/api/platform/engineer-reports/${report.id}/error-log/export?format=json`}
                data-testid="link-export-json"
              >
                <Button variant="outline" size="sm">
                  <Download className="h-4 w-4 mr-2" /> JSON
                </Button>
              </a>
            </div>
          </div>

          {errors.length === 0 ? (
            <EmptyState text="No errors captured in this window." />
          ) : (
            <div className="rounded-lg border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Severity</TableHead>
                    <TableHead>Message</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead className="text-right">Count</TableHead>
                    <TableHead className="text-right">WS</TableHead>
                    <TableHead>Repro trail</TableHead>
                    <TableHead>Repro</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {errors.map((e) => (
                    <TableRow key={e.signature} data-testid={`row-error-${e.signature.slice(0, 8)}`}>
                      <TableCell>
                        <Badge variant="secondary" className={SEV_STYLES[e.severity] ?? ""}>
                          {e.severity}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-xs">
                        <div className="font-mono text-xs truncate" title={e.sampleMessage}>
                          {e.title || e.sampleMessage}
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                          fe {e.sources?.frontend ?? 0} · be {e.sources?.backend ?? 0}
                          {e.statusCodes?.length > 0 && ` · [${e.statusCodes.join(",")}]`}
                          {e.browsers?.length > 0 && ` · ${e.browsers.join("/")}`}
                        </div>
                      </TableCell>
                      <TableCell className="font-mono text-xs max-w-[160px] truncate" title={e.sampleRoute ?? ""}>
                        {e.sampleRoute ?? "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{e.occurrences}</TableCell>
                      <TableCell className="text-right tabular-nums">{e.affectedWorkspaces}</TableCell>
                      <TableCell className="max-w-[220px]">
                        <div className="text-[10px] text-muted-foreground truncate" title={trailString(e.breadcrumbTrail)}>
                          {trailString(e.breadcrumbTrail)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {e.screenshotObjectPath ? (
                            <a
                              href={e.screenshotObjectPath}
                              target="_blank"
                              rel="noreferrer"
                              title="Screenshot"
                              data-testid={`link-shot-${e.signature.slice(0, 8)}`}
                            >
                              <ImageIcon className="h-4 w-4 text-primary" />
                            </a>
                          ) : (
                            <ImageIcon className="h-4 w-4 opacity-20" />
                          )}
                          {e.domSnapshotObjectPath ? (
                            <a
                              href={e.domSnapshotObjectPath}
                              target="_blank"
                              rel="noreferrer"
                              title="DOM snapshot"
                              data-testid={`link-dom-${e.signature.slice(0, 8)}`}
                            >
                              <FileCode2 className="h-4 w-4 text-primary" />
                            </a>
                          ) : (
                            <FileCode2 className="h-4 w-4 opacity-20" />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* Markdown brief (rendered as preformatted text) */}
        <TabsContent value="brief">
          {report.brief ? (
            <Card>
              <CardContent className="py-4">
                <pre className="text-sm whitespace-pre-wrap font-sans leading-relaxed" data-testid="text-brief">
                  {report.brief}
                </pre>
              </CardContent>
            </Card>
          ) : (
            <EmptyState text="No brief was produced." />
          )}
        </TabsContent>

        {/* AI-categorized findings */}
        <TabsContent value="errors">
          <CategoryFindings items={categories.errors} kind="errors" />
        </TabsContent>
        <TabsContent value="performance">
          <CategoryFindings items={categories.performance} kind="performance" />
          {(rollup.performance?.length ?? 0) > 0 && (
            <div className="rounded-lg border overflow-x-auto mt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Metric</TableHead>
                    <TableHead>Route</TableHead>
                    <TableHead className="text-right">n</TableHead>
                    <TableHead className="text-right">p50</TableHead>
                    <TableHead className="text-right">p95</TableHead>
                    <TableHead className="text-right">p99</TableHead>
                    <TableHead className="text-right">max</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rollup.performance!.map((p, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{p.metric}</TableCell>
                      <TableCell className="font-mono text-xs max-w-[200px] truncate" title={p.route ?? ""}>
                        {p.route ?? "—"}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{p.samples}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.p50Ms}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.p95Ms}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.p99Ms}</TableCell>
                      <TableCell className="text-right tabular-nums">{p.maxMs}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>
        <TabsContent value="ux">
          <CategoryFindings items={categories.uxFriction} kind="ux" />
          {(rollup.uxFriction?.topActions?.length ?? 0) > 0 && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-sm">Most frequent actions</CardTitle>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {rollup.uxFriction!.topActions!.map((a, i) => (
                  <Badge key={i} variant="secondary">
                    {a.category}:{a.actionType} · {a.count}
                  </Badge>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>
        <TabsContent value="optimization">
          <CategoryFindings items={categories.optimization} kind="optimization" />
          {(rollup.optimization?.errorDenseRoutes?.length ?? 0) > 0 && (
            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-sm">Error-dense routes</CardTitle>
                <CardDescription>Routes with the most error occurrences.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-1">
                {rollup.optimization!.errorDenseRoutes!.map((r, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="font-mono text-xs truncate">{r.route ?? "—"}</span>
                    <span className="tabular-nums text-muted-foreground">{r.errorOccurrences}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Copy the canonical agent-ready text — fetched from the server so it stays
// byte-identical to the alert email and the public copy page.
function CopyForAgentButton({ reportId }: { reportId: string }) {
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleCopy = async () => {
    setLoading(true);
    try {
      const res = await apiRequest(
        "GET",
        `/api/platform/engineer-reports/${reportId}/agent-context`,
      );
      const text = await res.text();
      let ok = false;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(text);
          ok = true;
        } catch {
          ok = false;
        }
      }
      if (!ok) {
        const ta = document.createElement("textarea");
        ta.value = text;
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand("copy");
        document.body.removeChild(ta);
      }
      if (ok) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        toast({ title: "Agent context copied", description: "Paste it into the Replit agent." });
      } else {
        throw new Error("clipboard blocked");
      }
    } catch {
      toast({ title: "Could not copy", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleCopy}
      disabled={loading}
      data-testid="button-copy-for-agent"
    >
      {copied ? (
        <Check className="h-4 w-4 mr-2 text-emerald-600" />
      ) : loading ? (
        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
      ) : (
        <ClipboardCopy className="h-4 w-4 mr-2" />
      )}
      Copy for Agent
    </Button>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardContent className="py-3">
        <div className="text-2xl font-semibold tabular-nums">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-dashed py-12 text-center text-sm text-muted-foreground">
      {text}
    </div>
  );
}

// Render Opus-categorized findings generically — fields vary by category but all
// share title/severity/recommendation, so we surface those plus any extras.
function CategoryFindings({ items, kind }: { items: any[] | undefined; kind: string }) {
  if (!Array.isArray(items) || items.length === 0) {
    return <EmptyState text="No findings in this category." />;
  }
  const extraKeys: Record<string, string[]> = {
    errors: ["signature", "occurrences", "likelyCause"],
    performance: ["route", "metric", "observation"],
    ux: ["pattern"],
    optimization: ["observation"],
  };
  const keys = extraKeys[kind] ?? [];
  return (
    <div className="space-y-3">
      {items.map((it, i) => (
        <Card key={i} data-testid={`finding-${kind}-${i}`}>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-2">
              <CardTitle className="text-base">{it.title || "(untitled)"}</CardTitle>
              {it.severity && (
                <Badge variant="secondary" className={SEV_STYLES[it.severity] ?? ""}>
                  {it.severity}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {keys.map((k) =>
              it[k] != null && String(it[k]).length > 0 ? (
                <div key={k}>
                  <span className="text-muted-foreground capitalize">{k}: </span>
                  <span className={k === "signature" ? "font-mono text-xs" : ""}>
                    {String(it[k])}
                  </span>
                </div>
              ) : null,
            )}
            {it.recommendation && (
              <div className="rounded bg-muted/60 px-3 py-2">
                <span className="font-medium">Recommendation: </span>
                {it.recommendation}
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
}
